"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { FadeIn } from "@/components/ui/fade-in";
import { cn } from "@/lib/utils";
import { DecodeText } from "@/components/ui/decode-text";
import { LandingFooter } from "@/components/ui/footer";
import { SiteNavigation } from "@/components/ui/site-navigation";
import { PillButton } from "@/components/ui/pill-button";
import { PromptTextarea } from "@/components/ui/prompt-textarea";
import { env } from "@/env";
import { ShowcaseCard } from "@/components/ui/showcase-card";
import { MuxVideo } from "@/components/ui/mux-video";
import type { VideoAssetKey } from "@/lib/video-assets";

const GRAIN_BG =
  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

function FeatureVideoCard({
  num,
  title,
  desc,
  videoAssetKey,
}: {
  num: string;
  title: string;
  desc: string;
  videoAssetKey: VideoAssetKey;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    setIsDesktop(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return (
    <div
      className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden hover:border-white/[0.15] transition-all duration-300 hover:shadow-[0_0_20px_rgba(255,255,255,0.04)]"
      onMouseEnter={() => {
        if (isDesktop) videoRef.current?.play();
      }}
      onMouseLeave={() => {
        if (isDesktop) {
          const v = videoRef.current;
          if (v) {
            v.pause();
            v.currentTime = 0;
          }
        }
      }}
    >
      <div className="aspect-square overflow-hidden relative">
        <MuxVideo
          ref={videoRef}
          assetKey={videoAssetKey}
          autoPlay={!isDesktop}
          className="w-full h-full object-cover brightness-[0.55] contrast-[0.95] group-hover:scale-[1.03] transition-transform duration-500 ease-out"
        />
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.14] mix-blend-overlay"
          style={{
            backgroundImage: GRAIN_BG,
            backgroundRepeat: "repeat",
            backgroundSize: "180px",
          }}
        />
      </div>
      <div className="p-7 flex flex-col gap-3">
        <span className="text-[11px] font-mono text-white/30">{num}</span>
        <h3 className="text-lg font-normal tracking-tight">{title}</h3>
        <p className="text-[13px] font-light text-white/35 leading-relaxed">
          {desc}
        </p>
      </div>
    </div>
  );
}

export function AgenticaRefreshPageContent() {
  const isDark = true;
  return (
    <div className="grain min-h-screen bg-[#09090a] text-white font-arizona-sans antialiased selection:bg-[#7678E3]/30 agentica-page-fade-in">
      <SiteNavigation isDark={isDark} activePage="agentica" />

      {/* Hero */}
      <section className="relative h-screen max-h-[750px] flex flex-col justify-center px-6 sm:px-10 pb-16 pt-32 overflow-hidden">
        <div
          className="relative z-10 max-w-7xl mx-auto w-full"
          style={{ transform: "translateY(30px)" }}
        >
          {/* Status badge */}
          <div className="flex items-center justify-center gap-2.5 mb-5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#7678E3] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#7678E3]" />
            </span>
            <span className="text-xs font-ibm-plex-mono font-normal tracking-wide uppercase text-white">
              <DecodeText text="Agentica: Beta Available Now" delay={0} />
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-[1.95rem] min-[386px]:text-4xl sm:text-[3.5rem] font-normal tracking-[-0.03em] leading-[1.05] mb-10 max-w-3xl text-center mx-auto">
            Long-running agents at
            <br />
            your fingertips
          </h1>

          {/* Small descriptor */}
          <div className="flex flex-col items-center gap-10 mb-12">
            <PromptTextarea
              className="max-w-2xl"
              onSubmit={(message) => {
                window.location.href = `${env.NEXT_PUBLIC_AGENTICA_BASE_URL}?q=${encodeURIComponent(message)}`;
              }}
            />
            <a href="#about">
              <PillButton variant="secondary" responsive icon="none">
                Check Out Demos
              </PillButton>
            </a>
          </div>
        </div>
      </section>

      {/* About — sticky headline + scrollable videos */}
      <FadeIn as="section" id="about" className="px-6 sm:px-10 py-32">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-16 lg:gap-20">
            {/* Left column — sticky */}
            <div className="lg:w-1/3 lg:sticky lg:top-32 lg:self-start">
              <h2 className="text-3xl sm:text-4xl font-normal tracking-[-0.03em] leading-[1.1] mb-8">
                From Prompt to Autonomous Agent
              </h2>
              <p className="text-sm font-light text-white/40 leading-relaxed mb-8">
                Agentica makes it possible to turn an idea into a working agent
                in minutes. Describe the task, connect the tools you need, and
                let the agent handle the rest - running continuously,
                coordinating sub-agents, and adapting as it works.
              </p>
              <a href={env.NEXT_PUBLIC_AGENTICA_BASE_URL}>
                <PillButton variant="primary" responsive>
                  Try Agentica
                </PillButton>
              </a>
            </div>

            {/* Right column — scrollable videos */}
            <div className="lg:w-2/3 flex flex-col gap-24">
              {(
                [
                  {
                    num: "01",
                    title: "San Francisco Rental Tracker",
                    desc: "A continuously running agent that monitors listings from Zillow and Apartments.com in San Francisco, constantly finding new properties as they appear. Properties are displayed on an interactive map and a clean, dynamic dashboard with large images and key details. Users can filter by price and get a weekly email summary with photos and links.",
                    videoAssetKey: "agentica/rental-tracker" as const,
                  },
                  {
                    num: "02",
                    title: "Team Travel Hub",
                    desc: "An always-on agent that connects to your team’s Slack and Outlook to manage travel in one place. It tracks trips, hotels, expenses, and your annual travel budget on a unified dashboard. Team members can chat with the agent in Slack to upload expenses or invoices, which are automatically logged and reflected in the dashboard and spend totals. The agent also discovers relevant tech and AI conferences, suggests attendees, and notifies the team about travel plans and bookings.",
                    videoAssetKey: "agentica/travel-hub" as const,
                  },
                  {
                    num: "03",
                    title: "Fashion Sales Finder",
                    desc: "Agentica monitors designer sample sales and major discount events across London. It automatically discovers upcoming and live sales from luxury brands, boutiques, and online retailers, adds them to your personal dashboard, and sends tailored email alerts so you never miss a deal. From exclusive sample events to flash discounts on your favourite brands, everything is tracked in one place.",
                    videoAssetKey: "agentica/sales-finder" as const,
                  },
                ] as const
              ).map((item) => (
                <ShowcaseCard key={item.num} {...item} />
              ))}
            </div>
          </div>
        </div>
      </FadeIn>

      {/* Features */}
      <FadeIn
        as="section"
        id="features"
        className="px-6 sm:px-10 py-32 border-t border-white/[0.06]"
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-20">
            <h2 className="text-3xl sm:text-4xl font-normal tracking-[-0.03em] leading-[1.1] max-w-lg">
              Agents by Anyone, for Everyone
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {(
              [
                {
                  num: "01",
                  title: "Long-running tasks",
                  desc: "Chat with Agentica to give it access to your apps, services, and accounts. Dispatch tasks that happen while you're away.",
                  videoAssetKey: "agentica/feature-01" as const,
                },
                {
                  num: "02",
                  title: "Interactive dashboard",
                  desc: "Iterate with your agent to build dynamic dashboards with live data from the internet or any of your apps.",
                  videoAssetKey: "agentica/feature-02" as const,
                },
                {
                  num: "03",
                  title: "Live data analysis",
                  desc: "Drop in your files to complete complex data science tasks and interact with large documents or CSVs.",
                  videoAssetKey: "agentica/feature-03" as const,
                },
              ] as const
            ).map((card) => (
              <FeatureVideoCard key={card.num} {...card} />
            ))}
          </div>
        </div>
      </FadeIn>

      {/* Open Source */}
      <FadeIn
        as="section"
        id="open-source"
        className="relative px-6 sm:px-10 py-32 border-t border-white/[0.06] overflow-hidden"
      >
        {/* Background image */}

        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-normal tracking-[-0.03em] leading-[1.1] mb-6">
            Agentica SDK is Open Source
          </h2>
          <p className="text-base font-light text-white/40 leading-relaxed max-w-2xl mx-auto mb-10">
            Build, extend, and contribute to the core agent framework. Agentica
            SDK gives you full access to the primitives that power
            production-grade AI agents — with transparent code you can audit,
            fork, and make your own.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="https://github.com/symbolica-ai/agentica-python-sdk"
              target="_blank"
              rel="noopener noreferrer"
            >
              <PillButton variant="primary" responsive icon="github">
                Agentica SDK
              </PillButton>
            </a>
            <Link
              href="/agentica-sdk"
              className="inline-flex items-center gap-2 text-xs sm:text-sm font-mono font-medium uppercase text-white border border-white/15 rounded-full px-4 sm:px-6 py-2 sm:py-3 hover:border-white/30 transition-colors duration-200"
            >
              Why We Built It
            </Link>
          </div>
        </div>
      </FadeIn>

      <LandingFooter colorMode={isDark ? "dark" : "light"} noMargin />
    </div>
  );
}
